import { useEffect, useRef, useState, useMemo } from "react";
import { to12h } from "./shared";
import { istNow } from "../lib/dates";

const toMin = (hhmm) => { const [h, m] = (hhmm || "0:0").split(":").map(Number); return h * 60 + m; };
const toHHMM = (mins) => `${String(Math.floor(mins / 60) % 24).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
const isoToday = () => { const d = istNow(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

const PX = 2.4;          // px per minute
const ROW_H = 60;        // staff row height
const HEAD_H = 34;       // time-axis header height
const SNAP = 15;         // minutes

const STATUS_COLOR = {
  Completed: "bg-green-900/50 border-green-600 text-green-100",
  Cancelled: "bg-red-900/40 border-red-700 text-red-200 line-through opacity-60",
  "No Show": "bg-red-900/40 border-red-700 text-red-200 opacity-60",
  Confirmed: "bg-primary/25 border-primary text-primary-foreground",
  "In Service": "bg-amber-900/50 border-amber-500 text-amber-100",
};

export default function TimelineCalendar({ branchId, date, staff, appointments, branchHours, onSlotClick, onApptClick }) {
  const scrollRef = useRef(null);
  const [nowMin, setNowMin] = useState(() => { const d = istNow(); return d.getHours() * 60 + d.getMinutes(); });
  // Axis always covers every appointment, even if booked outside the configured branch hours
  const [open, close] = useMemo(() => {
    let o = toMin(branchHours?.open || "09:00"), c = toMin(branchHours?.close || "21:00");
    for (const a of appointments) {
      const s = toMin(a.start_time); const e = a.end_time ? toMin(a.end_time) : s + (a.duration || 30);
      if (s < o) o = Math.floor(s / 60) * 60;
      if (e > c) c = Math.ceil(e / 60) * 60;
    }
    return [o, c];
  }, [branchHours?.open, branchHours?.close, appointments]);
  const totalMin = Math.max(60, close - open);
  const width = totalMin * PX;
  const isToday = date === isoToday();

  useEffect(() => {
    const t = setInterval(() => { const d = istNow(); setNowMin(d.getHours() * 60 + d.getMinutes()); }, 30000);
    return () => clearInterval(t);
  }, []);

  // Auto-scroll so the CURRENT time sits near the centre of the viewport (today only).
  // Runs once when the book opens / branch / date / staff changes — manual scroll works after.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (!isToday) { el.scrollLeft = 0; return; }
    const centre = () => {
      const vw = el.clientWidth || 0;
      el.scrollLeft = Math.max(0, (nowMin - open) * PX - vw / 2);
    };
    const raf = requestAnimationFrame(centre);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, date, staff.length]);

  const hourMarks = useMemo(() => {
    const marks = [];
    const startH = Math.floor(open / 60);
    const endH = Math.ceil(close / 60);
    for (let h = startH; h <= endH; h++) marks.push(h * 60);
    return marks.filter((m) => m >= open && m <= close);
  }, [open, close]);

  // Faint 15-minute gridlines (exclude the on-the-hour marks, which draw stronger)
  const quarterMarks = useMemo(() => {
    const marks = [];
    const start = Math.ceil(open / SNAP) * SNAP;
    for (let m = start; m <= close; m += SNAP) if (m % 60 !== 0) marks.push(m);
    return marks;
  }, [open, close]);

  const byStaff = useMemo(() => {
    const map = {};
    for (const a of appointments) {
      const sid = a.staff_id || "unassigned";
      (map[sid] = map[sid] || []).push(a);
    }
    return map;
  }, [appointments]);

  const nowX = (nowMin - open) * PX;

  const handleTrackClick = (e, s) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left; // row is full-width inside the scroller; rect already reflects scroll
    let mins = open + Math.round((x / PX) / SNAP) * SNAP;
    mins = Math.max(open, Math.min(close - SNAP, mins));
    if (isToday && mins < nowMin) return; // past slots not quick-bookable
    onSlotClick(s, toHHMM(mins), date);
  };

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid="timeline-calendar">
      <div className="flex">
        {/* sticky staff column */}
        <div className="shrink-0 w-40 sm:w-48 border-r border-border bg-secondary/40 z-20">
          <div style={{ height: HEAD_H }} className="border-b border-border flex items-center px-3 text-xs font-medium text-muted-foreground">Staff</div>
          {staff.map((s) => (
            <div key={s.id} style={{ height: ROW_H }} className="border-b border-border/60 flex items-center gap-2 px-3" data-testid={`tl-staff-${s.id}`}>
              {(s.photo || s.image)
                ? <img src={s.photo || s.image} alt="" className="h-8 w-8 rounded-full object-cover border border-primary/30 shrink-0" />
                : <span className="h-8 w-8 rounded-full bg-primary/20 text-primary text-xs grid place-items-center shrink-0">{(s.name || "?")[0]}</span>}
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{s.name}</div>
                {s.weekly_off_today && <div className="text-[10px] text-amber-400">weekly off</div>}
              </div>
            </div>
          ))}
          {!staff.length && <div className="p-4 text-sm text-muted-foreground">No active staff for this branch.</div>}
        </div>

        {/* scrollable time grid */}
        <div ref={scrollRef} className="flex-1 overflow-x-auto relative">
          <div style={{ width }} className="relative">
            {/* header */}
            <div style={{ height: HEAD_H }} className="relative border-b border-border">
              {hourMarks.map((m) => (
                <div key={m} className="absolute top-0 h-full flex items-center" style={{ left: (m - open) * PX }}>
                  <span className="text-[11px] text-muted-foreground px-1 -translate-x-1/2 whitespace-nowrap">{to12h(toHHMM(m))}</span>
                </div>
              ))}
            </div>

            {/* rows */}
            {staff.map((s) => (
              <div key={s.id} style={{ height: ROW_H }} className="relative border-b border-border/60 hover:bg-primary/5 cursor-pointer"
                onClick={(e) => handleTrackClick(e, s)} data-testid={`tl-row-${s.id}`}>
                {/* quarter-hour gridlines (faint) */}
                {quarterMarks.map((m) => <div key={`q${m}`} className="absolute top-0 bottom-0 border-l border-border/25" style={{ left: (m - open) * PX }} />)}
                {/* hour gridlines */}
                {hourMarks.map((m) => <div key={m} className="absolute top-0 bottom-0 border-l border-border/60" style={{ left: (m - open) * PX }} />)}
                {/* appointment blocks */}
                {(byStaff[s.id] || []).map((a) => {
                  const start = toMin(a.start_time); const end = toMin(a.end_time || toHHMM(start + (a.duration || 30)));
                  const left = (start - open) * PX; const w = Math.max(24, (end - start) * PX);
                  return (
                    <div key={a.id} onClick={(e) => { e.stopPropagation(); onApptClick && onApptClick(a); }}
                      className={`absolute top-1 bottom-1 rounded-md border px-1.5 py-0.5 overflow-hidden text-[11px] leading-tight ${onApptClick ? "cursor-pointer" : ""} ${STATUS_COLOR[a.status] || "bg-secondary border-border"}`}
                      style={{ left, width: w }} title={`${a.customer_name} · ${a.service_name} · ${to12h(a.start_time)}-${to12h(a.end_time)} · ${a.staff_name || ""}`}
                      data-testid={`tl-appt-${a.id}`}>
                      <div className="font-medium truncate">{a.customer_name}</div>
                      <div className="truncate opacity-80">{a.service_name} · {to12h(a.start_time)}</div>
                    </div>
                  );
                })}
              </div>
            ))}

            {/* Past-time shading (today only): visible but not highlighted as available */}
            {isToday && nowMin > open && staff.length > 0 && (
              <div className="absolute z-[5] pointer-events-none bg-black/25" data-testid="timeline-past-overlay"
                style={{ left: 0, top: HEAD_H, width: Math.max(0, (Math.min(nowMin, close) - open) * PX), height: staff.length * ROW_H }} />
            )}

            {/* NOW line */}
            {isToday && nowMin >= open && nowMin <= close && staff.length > 0 && (
              <div className="absolute z-10 pointer-events-none" style={{ left: nowX, top: 0, height: HEAD_H + staff.length * ROW_H }} data-testid="timeline-now-line">
                <div className="absolute top-0 -translate-x-1/2 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap">NOW – {to12h(toHHMM(nowMin))}</div>
                <div className="absolute top-[18px] bottom-0 w-[2px] bg-red-500" />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="px-3 py-2 text-[11px] text-muted-foreground border-t border-border">Tap any free future slot in a staff row to book. Past times can't be quick-booked.</div>
    </div>
  );
}
